import { useState, useEffect, useRef, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import BRAND from '../../config/branding';

const API_BASE = (import.meta.env.VITE_API_URL || 'http://localhost:5000/api').replace(/\/api$/, '');

const LIKERT_5_AGREE  = ['Strongly disagree', 'Disagree', 'Neither agree nor disagree', 'Agree', 'Strongly agree'];
const LIKERT_5_SAT    = ['Strongly dissatisfied', 'Dissatisfied', 'Neither dissatisfied or satisfied', 'Satisfied', 'Strongly satisfied'];
const LIKERT_7_LABELS = ['Strongly disagree', 'Disagree', 'Somewhat disagree', 'Neutral', 'Somewhat agree', 'Agree', 'Strongly agree'];

/* Dept → position options for engagement_assessment conditional filtering */
const DEPT_POSITION_MAP = {
  'Nursing':                ['RN (Registered Nurse)', 'LPN / LVN (Licensed Practical / Vocational Nurse)', 'CNA (Certified Nursing Assistant)', 'PSW (Personal Support Worker)', 'RPN (Registered Practical Nurse)', 'NP (Nurse Practitioner)', 'Charge Nurse', 'Unit Manager / Supervisor', 'DON / Director of Care', 'Other'],
  'Dietary & Food Services':['Dietary Aide', 'Cook / Chef', 'Dietary Manager', 'Other'],
  'Housekeeping & Laundry': ['Housekeeper', 'Laundry Aide', 'Environmental Services Manager', 'Other'],
  'Maintenance':            ['Maintenance Technician', 'Maintenance Supervisor', 'Other'],
  'Recreation & Activities':['Recreation / Activity Aide', 'Recreation / Activity Director', 'Other'],
  'Social Work':            ['Social Worker', 'Social Work Assistant', 'Social Work Director', 'Other'],
  'Rehabilitation':         ['Occupational Therapist (OT)', 'Physical Therapist (PT)', 'OT / PT Assistant (COTA / PTA)', 'Speech-Language Pathologist', 'Rehab Aide', 'Rehab Director', 'Other'],
  'Administration':         ['Administrative Assistant', 'Medical Records Clerk', 'Receptionist / Front Desk', 'HR Staff', 'Billing / Finance Staff', 'Office Manager', 'Other'],
  'Management':             ['Administrator / CEO', 'Assistant Administrator', 'Department Manager / Director', 'Other'],
  'Other':                  ['Other'],
};

function getLikertLabels(q) {
  if (q.type === 'likert_7') return LIKERT_7_LABELS;
  if (q.likertVariant === 'satisfaction') return LIKERT_5_SAT;
  return LIKERT_5_AGREE;
}

/* Group flat question list into pages.
   Priority:
   1) section_header-driven pages
   2) fallback chunking for templates without section headers */
function groupIntoPages(questions, fallbackChunkSize = 8) {
  const safeQuestions = Array.isArray(questions) ? questions : [];
  if (!safeQuestions.length) return [];

  const hasSectionHeaders = safeQuestions.some((q) => q?.type === 'section_header');
  if (hasSectionHeaders) {
    const pages = [];
    let current = null;
    for (const q of safeQuestions) {
      if (q.type === 'section_header') {
        current = { header: q, questions: [] };
        pages.push(current);
      } else if (current) {
        current.questions.push(q);
      } else {
        current = { header: null, questions: [q] };
        pages.push(current);
      }
    }
    return pages;
  }

  const chunkSize = Math.max(1, Number(fallbackChunkSize) || 8);
  const nonHeaderQuestions = safeQuestions.filter((q) => q?.type !== 'section_header');
  const pages = [];
  for (let i = 0; i < nonHeaderQuestions.length; i += chunkSize) {
    pages.push({ header: null, questions: nonHeaderQuestions.slice(i, i + chunkSize) });
  }
  return pages;
}

export default function SurveyPage() {
  const { token } = useParams();
  const [status, setStatus] = useState('loading'); // loading | ready | error | submitted
  const [errorMsg, setErrorMsg] = useState('');
  const [campaign, setCampaign] = useState(null);
  const [template, setTemplate] = useState(null);
  const [answers, setAnswers] = useState({});
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const topRef = useRef(null);
  const startedAt = useRef(new Date().toISOString());

  const isEngagement =
    template?.surveyType === 'engagement_assessment' ||
    campaign?.surveyType === 'engagement_assessment';
  const allQuestions = useMemo(() => template?.questions || [], [template]);

  const pages = useMemo(() => groupIntoPages(allQuestions), [allQuestions]);
  const organizationName = useMemo(
    () => String(campaign?.organizationName || '').trim(),
    [campaign?.organizationName],
  );
  const surveyDisplayTitle = useMemo(() => {
    const rawTemplateName = String(template?.name || '').trim();
    const placeholderPattern = /\{\{\s*organization(Name)?\s*\}\}|\{organization\/client'?s\}/gi;

    if (rawTemplateName) {
      if (organizationName) {
        return rawTemplateName.replace(placeholderPattern, organizationName);
      }
      return rawTemplateName.replace(placeholderPattern, 'Organization');
    }

    if (
      template?.surveyType === 'engagement_assessment' ||
      campaign?.surveyType === 'engagement_assessment'
    ) {
      return organizationName
        ? `${organizationName} Employee Engagement Survey`
        : 'Employee Engagement Survey';
    }

    return 'Survey';
  }, [template?.name, template?.surveyType, campaign?.surveyType, organizationName]);

  const totalPages = pages?.length || 1;
  const progressPct = Math.round(((currentPage + 1) / totalPages) * 100);

  useEffect(() => {
    if (!token) {
      setErrorMsg('No survey token provided.');
      setStatus('error');
      return;
    }
    fetch(`${API_BASE}/api/public/surveys/${token}`)
      .then((res) => res.json().then((data) => ({ ok: res.ok, httpStatus: res.status, data })))
      .then(({ ok, httpStatus, data }) => {
        if (!ok) {
          const msg =
            httpStatus === 409 ? 'This survey has already been completed. Responses cannot be submitted more than once.' :
            httpStatus === 410 ? 'This survey link has expired or the campaign has closed.' :
            httpStatus === 404 ? 'This survey link is not valid.' :
            data.error || 'Failed to load the survey.';
          throw new Error(msg);
        }
        setCampaign(data.campaign);
        setTemplate(data.template);
        setStatus('ready');
      })
      .catch((err) => {
        setErrorMsg(err.message || 'Failed to load the survey.');
        setStatus('error');
      });
  }, [token]);

  const setAnswer = (key, value) => setAnswers((prev) => ({ ...prev, [key]: value }));
  const clearError = (key) => setErrors((prev) => { const n = { ...prev }; delete n[key]; return n; });

  function validateQuestions(qs) {
    const errs = {};
    let firstKey = null;
    for (const q of qs) {
      if (q.type === 'section_header' || !q.required) continue;
      const val = answers[q.key];
      if (val === undefined || val === null || val === '' || (Array.isArray(val) && val.length === 0)) {
        errs[q.key] = 'This question is required.';
        if (!firstKey) firstKey = q.key;
      }
    }
    return { errs, firstKey };
  }

  function scrollTop() { topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }

  function handleNext() {
    const { errs, firstKey } = validateQuestions(pages[currentPage]?.questions || []);
    if (firstKey) {
      setErrors((prev) => ({ ...prev, ...errs }));
      document.getElementById(`q-${firstKey}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    setCurrentPage((p) => p + 1);
    scrollTop();
  }

  function handleBack() {
    setCurrentPage((p) => Math.max(0, p - 1));
    scrollTop();
  }

  async function doSubmit(pageQs) {
    const { errs, firstKey } = validateQuestions(pageQs);
    if (firstKey) {
      setErrors((prev) => ({ ...prev, ...errs }));
      document.getElementById(`q-${firstKey}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    setSubmitting(true);
    setErrorMsg('');
    try {
      const res = await fetch(`${API_BASE}/api/public/surveys/${token}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          answers: Object.entries(answers).map(([key, value]) => ({ key, value })),
          completion: { startedAt: startedAt.current, submittedAt: new Date().toISOString(), progressPct: 100 },
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Submission failed. Please try again.');
      setStatus('submitted');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setErrorMsg(err.message || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  // ── Question renderer ────────────────────────────────────────────────────────
  function renderQuestion(q, qNum) {
    const hasError = Boolean(errors[q.key]);
    const answer = answers[q.key];

    // Conditional options: filter position types by selected department
    const displayOptions = (() => {
      if (q.key === 'q2_position_type' && isEngagement) {
        const dept = answers['q1_dept'];
        return dept && DEPT_POSITION_MAP[dept] ? DEPT_POSITION_MAP[dept] : (q.options || []);
      }
      return q.options || [];
    })();

    return (
      <div
        id={`q-${q.key}`}
        key={`q-${q.key}`}
        className={`rounded-xl border bg-white px-5 py-4 shadow-sm transition-all ${
          hasError ? 'border-red-300 ring-1 ring-red-200' : 'border-gray-200'
        }`}
      >
        <p className="mb-3 text-sm font-medium leading-snug text-gray-900">
          <span className="mr-2 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-teal-100 text-xs font-bold text-teal-700">
            {qNum}
          </span>
          {q.label}
          {q.required && <span className="ml-1 text-red-500">*</span>}
        </p>

        {q.maxSelections && (
          <p className="mb-2 text-xs font-medium text-gray-400">Select up to {q.maxSelections}.</p>
        )}

        {/* Likert */}
        {(q.type === 'likert_5' || q.type === 'likert_7') && (
          <div className="space-y-1">
            {getLikertLabels(q).map((lbl, i) => {
              const val = String(i + 1);
              const selected = answer === val;
              return (
                <label
                  key={val}
                  className={`flex cursor-pointer items-center gap-3 rounded-lg border px-3 py-2 text-sm transition-colors ${
                    selected ? 'border-teal-400 bg-teal-50 text-teal-800' : 'border-gray-200 text-gray-700 hover:border-teal-200 hover:bg-gray-50'
                  }`}
                >
                  <input
                    type="radio" name={q.key} value={val} checked={selected}
                    onChange={() => { setAnswer(q.key, val); clearError(q.key); }}
                    className="accent-teal-600"
                  />
                  <span className={`w-4 shrink-0 text-center text-xs font-bold ${selected ? 'text-teal-600' : 'text-gray-400'}`}>{i + 1}</span>
                  <span>{lbl}</span>
                </label>
              );
            })}
          </div>
        )}

        {/* Single choice */}
        {q.type === 'single_choice' && (
          <div className="space-y-1">
            {displayOptions.map((opt, i) => {
              const selected = answer === opt;
              return (
                <label
                  key={i}
                  className={`flex cursor-pointer items-center gap-3 rounded-lg border px-3 py-2 text-sm transition-colors ${
                    selected ? 'border-teal-400 bg-teal-50 text-teal-800' : 'border-gray-200 text-gray-700 hover:border-teal-200 hover:bg-gray-50'
                  }`}
                >
                  <input
                    type="radio" name={q.key} value={opt} checked={selected}
                    onChange={() => {
                      setAnswer(q.key, opt);
                      clearError(q.key);
                      if (q.key === 'q1_dept') setAnswer('q2_position_type', undefined);
                    }}
                    className="accent-teal-600"
                  />
                  {opt}
                </label>
              );
            })}
          </div>
        )}

        {/* Dropdown */}
        {q.type === 'dropdown' && (
          <select
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-teal-400 focus:outline-none focus:ring-1 focus:ring-teal-400"
            value={answer || ''}
            onChange={(e) => { setAnswer(q.key, e.target.value); clearError(q.key); }}
          >
            <option value="">Select one option</option>
            {displayOptions.map((opt, i) => (
              <option key={`dd-${q.key}-${i}`} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        )}

        {/* NPS 0-10 */}
        {q.type === 'nps_11' && (
          <div>
            <div className="mb-1 flex items-center justify-between text-xs font-medium text-gray-500">
              <span>Not at all likely</span>
              <span>Extremely likely</span>
            </div>
            <div className="grid grid-cols-6 gap-2 sm:grid-cols-11">
              {Array.from({ length: 11 }, (_, i) => {
                const val = String(i);
                const selected = answer === val;
                return (
                  <label
                    key={`nps-${q.key}-${i}`}
                    className={`flex cursor-pointer items-center justify-center rounded-lg border px-0 py-2 text-sm font-semibold transition-colors ${
                      selected
                        ? 'border-teal-500 bg-teal-50 text-teal-800'
                        : 'border-teal-300 bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="radio"
                      name={q.key}
                      value={val}
                      checked={selected}
                      onChange={() => { setAnswer(q.key, val); clearError(q.key); }}
                      className="sr-only"
                    />
                    {i}
                  </label>
                );
              })}
            </div>
          </div>
        )}

        {/* Multi choice */}
        {q.type === 'multi_choice' && (
          <div className="space-y-1">
            {(q.options || []).map((opt, i) => {
              const checked = Array.isArray(answer) && answer.includes(opt);
              const atMax = Boolean(q.maxSelections) && Array.isArray(answer) && answer.length >= q.maxSelections && !checked;
              return (
                <label
                  key={i}
                  className={`flex items-center gap-3 rounded-lg border px-3 py-2 text-sm transition-colors ${
                    checked     ? 'cursor-pointer border-teal-400 bg-teal-50 text-teal-800'
                    : atMax     ? 'cursor-not-allowed border-gray-100 text-gray-400 opacity-60'
                                : 'cursor-pointer border-gray-200 text-gray-700 hover:border-teal-200 hover:bg-gray-50'
                  }`}
                >
                  <input
                    type="checkbox" checked={checked} disabled={atMax}
                    onChange={() => {
                      if (atMax) return;
                      const curr = Array.isArray(answer) ? answer : [];
                      setAnswer(q.key, checked ? curr.filter((v) => v !== opt) : [...curr, opt]);
                      clearError(q.key);
                    }}
                    className="accent-teal-600"
                  />
                  {opt}
                </label>
              );
            })}
          </div>
        )}

        {/* Checkboxes */}
        {q.type === 'checkboxes' && (
          <div className="space-y-1">
            {(q.options || []).map((opt, i) => {
              const checked = Array.isArray(answer) && answer.includes(opt);
              const atMax = Boolean(q.maxSelections) && Array.isArray(answer) && answer.length >= q.maxSelections && !checked;
              return (
                <label
                  key={`chk-${q.key}-${i}`}
                  className={`flex items-center gap-3 rounded-lg border px-3 py-2 text-sm transition-colors ${
                    checked     ? 'cursor-pointer border-teal-400 bg-teal-50 text-teal-800'
                    : atMax     ? 'cursor-not-allowed border-gray-100 text-gray-400 opacity-60'
                                : 'cursor-pointer border-gray-200 text-gray-700 hover:border-teal-200 hover:bg-gray-50'
                  }`}
                >
                  <input
                    type="checkbox" checked={checked} disabled={atMax}
                    onChange={() => {
                      if (atMax) return;
                      const curr = Array.isArray(answer) ? answer : [];
                      setAnswer(q.key, checked ? curr.filter((v) => v !== opt) : [...curr, opt]);
                      clearError(q.key);
                    }}
                    className="accent-teal-600"
                  />
                  {opt}
                </label>
              );
            })}
          </div>
        )}

        {/* Text */}
        {q.type === 'text' && (
          <textarea
            rows={3}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-teal-400 focus:outline-none focus:ring-1 focus:ring-teal-400"
            value={answer || ''}
            onChange={(e) => { setAnswer(q.key, e.target.value); clearError(q.key); }}
            placeholder="Your response…"
          />
        )}

        {/* Number */}
        {q.type === 'number' && (
          <input
            type="number" min={0}
            className="w-40 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-teal-400 focus:outline-none focus:ring-1 focus:ring-teal-400"
            value={answer ?? ''}
            onChange={(e) => { setAnswer(q.key, e.target.value); clearError(q.key); }}
          />
        )}

        {/* Boolean */}
        {q.type === 'boolean' && (
          <div className="flex gap-3">
            {['Yes', 'No'].map((opt) => {
              const selected = answer === opt;
              return (
                <label
                  key={opt}
                  className={`flex cursor-pointer items-center gap-2 rounded-lg border px-4 py-2 text-sm transition-colors ${
                    selected ? 'border-teal-400 bg-teal-50 text-teal-800' : 'border-gray-200 text-gray-700 hover:border-teal-200'
                  }`}
                >
                  <input
                    type="radio" name={q.key} value={opt} checked={selected}
                    onChange={() => { setAnswer(q.key, opt); clearError(q.key); }}
                    className="accent-teal-600"
                  />
                  {opt}
                </label>
              );
            })}
          </div>
        )}

        {hasError && <p className="mt-1.5 text-xs text-red-600">{errors[q.key]}</p>}
      </div>
    );
  }

  // ── Render states ────────────────────────────────────────────────────────────

  if (status === 'loading') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="mx-auto h-10 w-10 animate-spin rounded-full border-4 border-teal-600 border-t-transparent" />
          <p className="mt-4 text-sm text-gray-500">Loading survey...</p>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="flex min-h-screen flex-col bg-gray-50">
        <SurveyHeader />
        <main className="flex flex-1 items-center justify-center px-4 py-12">
          <div className="w-full max-w-md rounded-xl border border-red-200 bg-white p-8 text-center shadow-sm">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-red-100">
              <svg className="h-7 w-7 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="text-lg font-semibold text-gray-900">Survey Unavailable</h2>
            <p className="mt-2 text-sm text-gray-600">{errorMsg}</p>
          </div>
        </main>
        <SurveyFooter />
      </div>
    );
  }

  if (status === 'submitted') {
    return (
      <div className="flex min-h-screen flex-col bg-gray-50">
        <SurveyHeader />
        <main className="flex flex-1 items-center justify-center px-4 py-12">
          <div className="w-full max-w-md rounded-xl border border-teal-200 bg-white p-8 text-center shadow-sm">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-teal-100">
              <svg className="h-7 w-7 text-teal-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-gray-900">Thank you!</h2>
            <p className="mt-2 text-sm text-gray-600">
              Your response has been recorded. Your feedback helps improve workplace conditions for everyone.
            </p>
            {campaign?.organizationName && (
              <p className="mt-3 text-xs font-semibold text-teal-600">{campaign.organizationName}</p>
            )}
          </div>
        </main>
        <SurveyFooter />
      </div>
    );
  }

  // ── Ready — multi-page wizard (engagement_assessment) ────────────────────────
  if (pages && pages.length > 0) {
    const page = pages[currentPage];
    const isLastPage = currentPage === totalPages - 1;
    const prevQCount = pages.slice(0, currentPage).reduce((sum, p) => sum + p.questions.length, 0);
    let qCounter = prevQCount;

    return (
      <div className="flex min-h-screen flex-col bg-gray-50">
        <SurveyHeader />
        <div ref={topRef} />

        {/* Sticky progress bar */}
        <div className="sticky top-0 z-10 border-b border-gray-100 bg-white shadow-sm">
          <div className="mx-auto max-w-2xl px-4 py-2.5">
            <div className="mb-1.5 flex items-center justify-between">
              <span className="text-xs font-semibold text-gray-600">Step {currentPage + 1} of {totalPages}</span>
              <span className="text-xs font-bold text-teal-600">{progressPct}% complete</span>
            </div>
            <div className="h-2 w-full overflow-hidden rounded-full bg-gray-100">
              <div
                className="h-2 rounded-full bg-gradient-to-r from-teal-500 to-teal-400 transition-all duration-500"
                style={{ width: `${progressPct}%` }}
              />
            </div>
          </div>
        </div>

        <main className="flex-1 px-4 py-6">
          <div className="mx-auto w-full max-w-2xl">
            {/* Survey title block — first page only */}
            {currentPage === 0 && (
              <div className="mb-6 rounded-xl border border-teal-100 bg-white px-6 py-5 shadow-sm">
                <h1 className="text-xl font-bold text-gray-900">{surveyDisplayTitle}</h1>
                {organizationName && (
                  <p className="mt-0.5 text-sm font-semibold text-teal-700">{organizationName}</p>
                )}
                {template?.description && (
                  <p className="mt-1 text-sm text-gray-500">{template.description}</p>
                )}
                {template?.instructions && (
                  <div className="mt-3 rounded-lg bg-teal-50 px-4 py-3 text-sm text-teal-800">
                    {template.instructions}
                  </div>
                )}
                {campaign?.window?.closesAt && (
                  <p className="mt-3 text-xs text-gray-400">
                    Closes {new Date(campaign.window.closesAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                  </p>
                )}
              </div>
            )}

            {/* Section header */}
            {page?.header && (
              <div className="mb-5 border-b-2 border-teal-200 pb-3">
                <h2 className="text-base font-bold text-teal-800">{page.header.label}</h2>
                {page.header.description && (
                  <p className="mt-1 text-xs italic text-gray-500">{page.header.description}</p>
                )}
              </div>
            )}

            {errorMsg && (
              <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{errorMsg}</div>
            )}

            <div className="space-y-4">
              {(page?.questions || []).map((q) => {
                qCounter += 1;
                return renderQuestion(q, qCounter);
              })}
            </div>

            {/* Navigation */}
            <div className="mt-8 flex items-center justify-between">
              <button
                type="button"
                onClick={handleBack}
                disabled={currentPage === 0}
                className="rounded-lg border border-gray-300 bg-white px-6 py-2.5 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-40"
              >
                &larr; Back
              </button>
              {isLastPage ? (
                <button
                  type="button"
                  onClick={() => doSubmit(page?.questions || [])}
                  disabled={submitting}
                  className="rounded-lg bg-teal-600 px-8 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-teal-700 disabled:opacity-60"
                >
                  {submitting ? 'Submitting...' : 'Submit Survey'}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleNext}
                  className="rounded-lg bg-teal-600 px-8 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-teal-700"
                >
                  Next &rarr;
                </button>
              )}
            </div>
          </div>
        </main>
        <SurveyFooter />
      </div>
    );
  }

  // ── Ready — generic single-page form (non-engagement surveys) ────────────────
  let qNum = 0;
  return (
    <div className="flex min-h-screen flex-col bg-gray-50">
      <SurveyHeader />
      <main className="flex-1 px-4 py-8">
        <div className="mx-auto w-full max-w-2xl">
          <div className="mb-6 rounded-xl border border-teal-200 bg-white px-6 py-5 shadow-sm">
            <h1 className="text-xl font-bold text-gray-900">{surveyDisplayTitle}</h1>
            {template?.description && (
              <p className="mt-1 text-sm text-gray-500">{template.description}</p>
            )}
            {template?.instructions && (
              <div className="mt-3 rounded-lg bg-teal-50 px-4 py-3 text-sm text-teal-800">
                {template.instructions}
              </div>
            )}
            {campaign?.window?.closesAt && (
              <p className="mt-3 text-xs text-gray-400">
                This survey closes on{' '}
                {new Date(campaign.window.closesAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}.
              </p>
            )}
          </div>

          {errorMsg && (
            <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{errorMsg}</div>
          )}

          <form
            onSubmit={(e) => {
              e.preventDefault();
              doSubmit(allQuestions.filter((q) => q.type !== 'section_header'));
            }}
            noValidate
          >
            <div className="space-y-4">
              {allQuestions.map((q, idx) => {
                if (q.type === 'section_header') {
                  return (
                    <div key={`sec-${q.key}-${idx}`} className="pt-4">
                      <div className="border-b-2 border-teal-200 pb-2">
                        <h2 className="text-base font-bold text-teal-800">{q.label}</h2>
                        {q.description && (
                          <p className="mt-1 text-xs italic text-gray-500">{q.description}</p>
                        )}
                      </div>
                    </div>
                  );
                }
                qNum += 1;
                return renderQuestion(q, qNum);
              })}
            </div>
            <div className="mt-8 flex justify-end">
              <button
                type="submit"
                disabled={submitting}
                className="rounded-lg bg-teal-600 px-8 py-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-teal-700 disabled:opacity-60"
              >
                {submitting ? 'Submitting...' : 'Submit Survey'}
              </button>
            </div>
          </form>
        </div>
      </main>
      <SurveyFooter />
    </div>
  );
}

function SurveyHeader() {
  return (
    <header style={{ background: '#0d1b2a', position: 'relative', overflow: 'hidden' }}>
      {/* Abstract geometric blocks */}
      <div style={{ position: 'absolute', top: '-40px', right: '-50px', width: '220px', height: '160px', background: 'rgba(3,188,166,0.18)', borderRadius: '12px', transform: 'rotate(18deg)' }} />
      <div style={{ position: 'absolute', bottom: '-30px', right: '15%', width: '140px', height: '100px', background: 'rgba(3,188,166,0.10)', borderRadius: '8px', transform: 'rotate(-8deg)' }} />
      <div style={{ position: 'absolute', top: '10px', left: '-30px', width: '180px', height: '120px', background: 'rgba(255,255,255,0.04)', borderRadius: '10px', transform: 'rotate(12deg)' }} />
      <div style={{ position: 'absolute', bottom: '-10px', left: '38%', width: '60px', height: '60px', background: 'rgba(3,188,166,0.12)', borderRadius: '6px', transform: 'rotate(30deg)' }} />
      <div style={{ position: 'absolute', top: '50%', left: '8%', width: '28px', height: '28px', background: 'rgba(255,255,255,0.06)', borderRadius: '4px', transform: 'translateY(-50%) rotate(20deg)' }} />
      {/* Logo row */}
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '18px', padding: '36px 24px' }}>
        <img
          src="/stratel-favicon-dark.png"
          alt={`${BRAND.name} icon`}
          style={{ height: '56px', width: '56px', objectFit: 'contain', flexShrink: 0 }}
        />
        <div>
          <p style={{ fontSize: '2.25rem', fontWeight: 800, color: '#ffffff', letterSpacing: '-0.01em', lineHeight: 1.1 }}>{BRAND.name}</p>
          <p style={{ fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.5)', marginTop: '3px' }}>{BRAND.tagline}</p>
        </div>
      </div>
    </header>
  );
}

function SurveyFooter() {
  return (
    <footer className="mt-8 border-t border-gray-100 bg-white py-4 text-center text-xs text-gray-400">
      &copy; {new Date().getFullYear()} {BRAND.name} &mdash; Confidential survey powered by {BRAND.name}
    </footer>
  );
}
